import * as React from "react"
import { CatalogView } from "./catalog/CatalogView"
import { DetailView } from "./detail/DetailView"
import { HRIS_VIEWS, type HrisView } from "./detail/hris/HrisPanel"
import type { HrisEmployeeFilter } from "@/features/integrations/hris/hrisApi"
import { ACCOUNTING_VIEWS, type AccountingView } from "@/features/integrations/accounting/accountingApi"

const SHELL_ROOT = "/integrations"

type Props = {
  onNavigate?: (page: string) => void
  /** Override base path — defaults to /app/integrations (shell mode). */
  rootPath?: string
}

function getDetailIdFromPath(rootPath: string): string | null {
  const path = window.location.pathname
  // Match exactly the root or root + /:id, ignoring trailing slashes.
  const normalised = path.replace(/\/+$/, "")
  const root = rootPath.replace(/\/+$/, "")
  if (normalised === root) return null
  if (!normalised.startsWith(`${root}/`)) return null
  const tail = normalised.slice(root.length + 1)
  const id = tail.split("/")[0]
  return id || null
}

/** Second path segment (/integrations/lucca/employees) + `tab` query, for HR-system and ledger pages. */
function getSubViewFromPath(rootPath: string): { view: HrisView; accountingView: AccountingView; filter?: HrisEmployeeFilter } {
  const root = rootPath.replace(/\/+$/, "")
  const tail = window.location.pathname.replace(/\/+$/, "").slice(root.length + 1).split("/")
  const view: HrisView = (HRIS_VIEWS as readonly string[]).includes(tail[1]) ? (tail[1] as HrisView) : "overview"
  const accountingView: AccountingView = (ACCOUNTING_VIEWS as readonly string[]).includes(tail[1]) ? (tail[1] as AccountingView) : "overview"
  const tab = new URLSearchParams(window.location.search).get("tab")
  return { view, accountingView, filter: (tab as HrisEmployeeFilter | null) ?? undefined }
}

function scrollToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" })
}

export function IntegrationsPage({ rootPath = SHELL_ROOT }: Props) {
  const [detailId, setDetailId] = React.useState<string | null>(() =>
    getDetailIdFromPath(rootPath),
  )
  const [subView, setSubView] = React.useState(() => getSubViewFromPath(rootPath))

  React.useEffect(() => {
    const handlePopState = () => {
      setDetailId(getDetailIdFromPath(rootPath))
      setSubView(getSubViewFromPath(rootPath))
      scrollToTop()
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [rootPath])

  const goToDetail = React.useCallback(
    (id: string) => {
      window.history.pushState(null, "", `${rootPath}/${id}`)
      setDetailId(id)
      setSubView({ view: "overview", accountingView: "overview" })
      scrollToTop()
    },
    [rootPath],
  )

  const goToCatalog = React.useCallback(() => {
    window.history.pushState(null, "", rootPath)
    setDetailId(null)
    scrollToTop()
  }, [rootPath])

  const goToSubView = React.useCallback(
    (view: HrisView, filter?: HrisEmployeeFilter) => {
      if (!detailId) return
      const path = view === "overview" ? `${rootPath}/${detailId}` : `${rootPath}/${detailId}/${view}`
      window.history.pushState(null, "", filter ? `${path}?tab=${filter}` : path)
      setSubView((current) => ({ ...current, view, filter }))
      scrollToTop()
    },
    [detailId, rootPath],
  )

  const goToAccountingView = React.useCallback(
    (view: AccountingView) => {
      if (!detailId) return
      window.history.pushState(null, "", view === "overview" ? `${rootPath}/${detailId}` : `${rootPath}/${detailId}/${view}`)
      setSubView((current) => ({ ...current, accountingView: view }))
      scrollToTop()
    },
    [detailId, rootPath],
  )

  if (detailId) {
    return (
      <DetailView
        integrationId={detailId}
        onBack={goToCatalog}
        hrisView={subView.view}
        hrisFilter={subView.filter}
        onHrisNavigate={goToSubView}
        accountingView={subView.accountingView}
        onAccountingNavigate={goToAccountingView}
      />
    )
  }

  return <CatalogView onOpenDetail={goToDetail} />
}
